import React, { useState, useEffect } from "react";
import { Form } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { verifyOtp } from "../../../slices/auth/register/thunk";
import { resetRegisterState } from "../../../slices/auth/register/reducer";
import { showSuccessToast, showErrorToast } from "../../../helpers/toast_helper";
import appConfig from "../../../config";
import withRouter from "../../../Components/Common/withRouter";
import DashboardHeader from "../../../Components/Common/DashboardHeader";
import AuthLeftPanel from "../../../Components/Common/AuthLeftPanel";
import OTPInput from "../../../Components/Common/OTPInput";
import AuthFormButton from "../../../Components/Common/AuthFormButton";
import AuthLink from "../../../Components/Common/AuthLink";

const RegisterOTP = () => {
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const [loader, setLoader] = useState<boolean>(false);
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '']);

  const [phoneData] = useState({
    phoneno: sessionStorage.getItem("phoneNumber") || '',
    dialcode: sessionStorage.getItem("dialcode") || appConfig.country.defaultDialCode,
    countrycode: sessionStorage.getItem("countrycode") || appConfig.country.defaultCountryCode,
  });

  const selectRegisterState = (state: any) => state.Register;
  const selectRegisterData = createSelector(selectRegisterState, (register) => ({
    loading: register.loading,
    successMsg: register.successMsg,
    errorMsg: register.errorMsg,
    otpVerified: register.otpVerified,
  }));

  const { loading, successMsg, errorMsg, otpVerified } = useSelector(selectRegisterData);

  useEffect(() => {
    if (!phoneData.phoneno) {
      navigate("/register");
    }
  }, [phoneData.phoneno, navigate]);

  useEffect(() => {
    if (successMsg && otpVerified) {
      showSuccessToast(successMsg, "otp-verification-success");
      setLoader(false);
      navigate("/create-account");
      dispatch(resetRegisterState());
    }
  }, [successMsg, otpVerified, navigate, dispatch]);

  useEffect(() => {
    if (errorMsg) {
      showErrorToast(errorMsg, "otp-verification-error");
      setLoader(false);
      dispatch(resetRegisterState());
    }
  }, [errorMsg, dispatch]);

  const otpValidation = useFormik({
    enableReinitialize: true,
    initialValues: { otp: otp.join('') },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required("Please enter OTP")
        .length(5, "Invalid OTP")
        .matches(/^[0-9]{5}$/, "OTP must contain exactly 5 numbers"),
    }),
    onSubmit: async () => {
      setLoader(true);
      try {
        await dispatch(verifyOtp({
          phoneno: phoneData.phoneno,
          dialcode: phoneData.dialcode,
          countrycode: phoneData.countrycode,
          otp: otp.join(''),
        }));
      } catch (error: any) {
        console.error("OTP verification error:", error);
        setLoader(false);
      }
    },
  });

  document.title = "AMANAT - Register OTP";

  return (
    <div className="auth-container">
      <DashboardHeader />
      <div className="auth-main-content">
        <div className="auth-panel-container">
          <AuthLeftPanel
            title="Register"
            description="Welcome to AMANAT. Enter the OTP sent to your mobile number."
          />
          <div className="auth-right-panel">
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                otpValidation.handleSubmit();
                return false;
              }}
              className="auth-form"
            >
              <OTPInput
                value={otp}
                onChange={setOtp}
                error={otpValidation.errors.otp}
                touched={otpValidation.touched.otp}
                onComplete={(otpString) => {
                  otpValidation.setFieldValue('otp', otpString);
                }}
              />
              <div className="auth-form-group">
                <AuthFormButton
                  loading={loader || loading}
                  type="submit"
                  onClick={() => {
                    otpValidation.setFieldValue('otp', otp.join(''));
                    otpValidation.handleSubmit();
                  }}
                >
                  Continue
                </AuthFormButton>
              </div>
            </Form>
            <AuthLink to="/register" text="Back to" linkText="Register" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(RegisterOTP);

